<template>
  <template v-if="$route.name === 'grade-sheets.index'" >
    <div v-can="'grade_sheets.display'">
      <GradeSheetFilter 
        :filters="filters"
        :classes="classes"
        :subjects="subjects"
        @update:filters="updateFilters"
        @apply-filters="applyFilters"
        @reset-filters="resetFilters"
        @add="navigateToAdd"
      />
      
      <GradeSheetsTable
        :gradeSheets="gradeSheets"
        :loading="isLoading"
        @delete="openDeleteDialog"
      />
      
      <PaginationControls
        v-if="pagination.totalItems > 0"
        :pagination="pagination"
        @page-changed="handlePageChange"
        @per-page-changed="handlePerPageChange"
      />
      
      <DeleteGradeSheetDialog
        ref="deleteDialog"
        @delete="deleteGradeSheet"
      />
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import GradeSheetsTable from '@/components/features/grade-sheets/GradeSheetTable.vue';
import GradeSheetFilter from '@/components/features/grade-sheets/GradeSheetFilter.vue';
import DeleteGradeSheetDialog from '@/components/features/grade-sheets/dialogs/GradeSheetDeleteDialog.vue';
import PaginationControls from '@/components/common/PaginationControls.vue';
import api from '@/api';

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const store = useStore();
const deleteDialog = ref(null);
const isLoading = ref(false);

const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');

const filters = ref({
  search: '',
  class_id: '',
  subject_id: '',
  academic_year_id: ''
});
const classes = ref([]);
const subjects = ref([]);
const teacherClasses = ref([]);

const gradeSheets = computed(() => {
  return store.getters['gradeSheets/getGradeSheets'] || [];
});

const pagination = computed(() => store.getters['gradeSheets/getPagination'] || {
  currentPage: 1,
  perPage: 10,
  totalItems: 0
});

function extractApiMessage(error, t, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const status = error?.response?.status;
  if (status === 401) {
    toast.error(t('auth.unauthorized', 'Unauthorized'));
    return;
  }
  if (status === 403) {
    toast.error(t('auth.forbidden', 'Forbidden'));
    return;
  }
  toast.error(extractApiMessage(error, t, fallbackKey));
}

const updateFilters = (newFilters) => {
  filters.value = newFilters;
  // Update URL query parameters
  updateRouteQuery();
};

const applyFilters = () => {
  fetchGradeSheets();
};

const updateRouteQuery = () => {
  const query = {};
  if (filters.value.search) query.search = filters.value.search;
  if (filters.value.class_id) query.class_id = filters.value.class_id;
  if (filters.value.subject_id) query.subject_id = filters.value.subject_id;
  if (filters.value.academic_year_id) query.academic_year_id = filters.value.academic_year_id;
  
  router.replace({ query });
};

const fetchGradeSheets = async () => {
  isLoading.value = true;
  try {
    const params = {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage,
      search: filters.value.search,
      class_id: filters.value.class_id,
      subject_id: filters.value.subject_id,
      academic_year_id: filters.value.academic_year_id
    };

    if (isTeacher.value) {
      params.teacher_only = 1;
      params.teacher_id = currentUser.value.id;
    }

    await store.dispatch('gradeSheets/fetchGradeSheets', params);
  } catch (error) {
    notifyError(error, 'gradeSheets.loadError');
    console.error('Error fetching grade sheets:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchTeacherClasses = async () => {
  try {
    const response = await api.get(`/teachers/${currentUser.value.id}/classes`);
    teacherClasses.value = response.data.data || [];
    
    if (teacherClasses.value.length === 1) {
      filters.value.class_id = teacherClasses.value[0].id;
    }
  } catch (error) {
    console.error(error);
    notifyError(error, 'gradeSheets.teacherClassesLoadError');
  }
};

const fetchClassesAndSubjects = async () => {
  try {
    if (isTeacher.value) {
      await fetchTeacherClasses();
      classes.value = teacherClasses.value;
      
      const subjectsPromises = teacherClasses.value.map(cls => api.get(`/classes/${cls.id}/subjects`));
      const subjectsResponses = await Promise.all(subjectsPromises);
      
      subjects.value = subjectsResponses
        .flatMap(res => 
          (res.data.data || []).map(item => ({
            id: item.subject_id,
            name: item.subject?.name || t('gradeSheets.subjectPlaceholder', { id: item.subject_id })
          }))
        )
        .filter((subject, index, self) => index === self.findIndex(s => s.id === subject.id));
    } else {
      const [classesRes, subjectsRes] = await Promise.all([api.get('/classes'), api.get('/subjects')]);
      classes.value = classesRes.data.data || [];
      subjects.value = subjectsRes.data.data || [];
    }
  } catch (error) {
    console.error(error);
    classes.value = [];
    subjects.value = [];
    notifyError(error, 'gradeSheets.classesSubjectsLoadError');
  }
};

const resetFilters = () => {
  const currentYear = store.getters['gradeSheets/getCurrentAcademicYear'];
  
  filters.value = {
    search: '',
    class_id: isTeacher.value && teacherClasses.value.length === 1 ? teacherClasses.value[0].id : '',
    subject_id: '',
    academic_year_id: currentYear ? currentYear.id : ''
  };
  
  updateRouteQuery();
  fetchGradeSheets();
};

const handlePageChange = (page) => {
  store.dispatch('gradeSheets/fetchGradeSheets', { 
    page,
    per_page: pagination.value.perPage,
    ...filters.value
  }).catch(err => notifyError(err, 'gradeSheets.loadError'));
};

const handlePerPageChange = (perPage) => {
  store.dispatch('gradeSheets/fetchGradeSheets', { 
    page: 1,
    per_page: perPage,
    ...filters.value
  }).catch(err => notifyError(err, 'gradeSheets.loadError'));
};

const openDeleteDialog = (gradeSheet) => {
  if (!gradeSheet?.id) {
    toast.error(t('gradeSheets.invalidDelete'));
    return;
  }
  deleteDialog.value?.open(gradeSheet);
};

const deleteGradeSheet = async (id) => {
  if (!id) {
    toast.error(t('gradeSheets.noIdDelete'));
    return;
  }

  isLoading.value = true;
  try {
    await store.dispatch('gradeSheets/deleteGradeSheet', id);
    toast.success(t('gradeSheets.deleteSuccess'));
    
    const currentPage = pagination.value.totalItems === 1 && pagination.value.currentPage > 1 
      ? pagination.value.currentPage - 1 
      : pagination.value.currentPage;
    
    await store.dispatch('gradeSheets/fetchGradeSheets', { 
      page: currentPage,
      per_page: pagination.value.perPage,
      ...filters.value
    });
  } catch (error) {
    if (error?.response?.status === 404) {
      await fetchGradeSheets();
      toast.error(t('gradeSheets.notFound', 'Not found'));
    } else {
      notifyError(error, 'gradeSheets.deleteError');
    }
  } finally {
    isLoading.value = false;
  }
};

const navigateToAdd = () => {
  router.push({ name: 'AddGradeSheet' });
};

// Load filters from URL on mount
const loadFiltersFromRoute = () => {
  if (route.query.search) filters.value.search = route.query.search;
  if (route.query.class_id) filters.value.class_id = route.query.class_id;
  if (route.query.subject_id) filters.value.subject_id = route.query.subject_id;
  if (route.query.academic_year_id) filters.value.academic_year_id = route.query.academic_year_id;
};

onMounted(async () => {
  // Fetch academic years first
  try {
    await store.dispatch('gradeSheets/fetchAcademicYears');
    const currentYear = store.getters['gradeSheets/getCurrentAcademicYear'];
    
    // Set default academic year if not in URL
    if (!route.query.academic_year_id && currentYear) {
      filters.value.academic_year_id = currentYear.id;
    }
  } catch (error) {
    console.error('Error fetching academic years:', error);
  }
  
  await fetchClassesAndSubjects();
  loadFiltersFromRoute();
  await fetchGradeSheets();
});

// Watch for route changes to update filters
watch(
  () => route.query,
  (newQuery) => {
    if (route.name === 'grade-sheets.index') {
      filters.value.search = newQuery.search || '';
      filters.value.class_id = newQuery.class_id || '';
      filters.value.subject_id = newQuery.subject_id || '';
      filters.value.academic_year_id = newQuery.academic_year_id || '';
      
      fetchGradeSheets();
    }
  },
  { immediate: true }
);
</script>